import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
} from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import theme from "../theme";
import axios from "axios";
import { STORE_ID } from "../Config/Constants";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import AssignedTickets from "./AssignedTickets";
import { useLocation } from "react-router-dom";
import LoadingScreen from "./LoadingScreen";
import RaisedTickets from "./RaisedTickets";

const MyTickets = ({ handleStaffNameOnHeader }) => {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const location = useLocation();
  const locationUserID = location?.state?.staff_id;

  if (locationUserID) {
    handleStaffNameOnHeader(location.state.staff_name);
  } else {
    handleStaffNameOnHeader(null);
  }

  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;
  const user_id = locationUserID ? locationUserID : userInfo?.id;

  const [apiToggle, setApiToggle] = useState(false);
  const [assignedTicket, setAssignedTicket] = useState([]);
  const [assignedToTicket, setAssignedToTicket] = useState([]);
  const [followUpTickets, setFollowUpTickets] = useState([]);
  const [raisedTicket, setRaisedTicket] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState(store_id !== STORE_ID ? 0 : 1);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef([]);
  const tabs = ["Internal", "External"];
  const [serviceType, setServiceType] = useState("external");

  // NEW: status filter for follow‑up tab – default is "follow_up"
  const [followUpStatus, setFollowUpStatus] = useState("follow_up");

  useLayoutEffect(() => {
    if (tabRefs.current[activeTab]) {
      const el = tabRefs.current[activeTab];
      setIndicatorStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeTab]);

  // Main effect – fetches assigned & assigned‑to tickets
  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const urlForAssigned = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/ticketbyassigneduser?assigned_to=${user_id}`;
        const urlFollowUpBy = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/ticketbyassignedfromuser?assigned_from=${user_id}`;
        const urlRaised = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/ticketbyuser?createdby=${user_id}&store_id=${store_id}`;

        const promises = [
          axios.get(urlForAssigned), // index 0
          axios.get(urlFollowUpBy), // index 1
        ];

        if (store_id !== STORE_ID) {
          promises.push(axios.get(urlRaised)); // index 2
        }

        const results = await Promise.allSettled(promises);

        if (results[0].status === "fulfilled") {
          setAssignedTicket(results[0].value.data.data || []);
        } else {
          console.error("❌ Assigned tickets failed", results[0].reason);
        }

        if (results[1].status === "fulfilled") {
          setAssignedToTicket(results[1].value.data.data || []);
        } else {
          console.error("❌ AssignedFrom tickets failed", results[1].reason);
        }

        if (store_id !== STORE_ID && results[2]?.status === "fulfilled") {
          setRaisedTicket(results[2].value.data.data || []);
        }
      } catch (error) {
        console.error("❌ Unexpected error fetching tickets", error);
      } finally {
        setLoading(false);
      }
    };

    if (user_id) fetchTickets();
  }, [user_id, store_id, apiToggle]);

  // Dedicated effect for follow‑up tickets – now also depends on apiToggle!
  useEffect(() => {
    const fetchFollowUp = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/followup/tickets/${user_id}?status=${followUpStatus}`;
        const { data } = await axios.get(url);
        if (data.statusCode === 200) {
          setFollowUpTickets(data.data || []);
        }
      } catch (error) {
        console.error("❌ Follow-up fetch failed", error);
      }
    };

    if (user_id) fetchFollowUp();
  }, [user_id, followUpStatus, apiToggle]); // ✅ Added apiToggle

  const handleServiceChange = (newValue) => setServiceType(newValue);

  if (loading) return <LoadingScreen loadingMessage={"Loading tickets"} />;

  if (store_id === STORE_ID) {
    return (
      <AssignedTickets
        setApiToggle={setApiToggle}
        locationUserID={locationUserID}
        tickets={assignedTicket}
        assignedToTicket={assignedToTicket}
        followUpTickets={followUpTickets}
        user_id={user_id}
        store_id={store_id}
        followUpStatus={followUpStatus}
        setFollowUpStatus={setFollowUpStatus}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          width: isMobile ? "100vw" : "100%",
          mx: -2,
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          px: 1,
          py: 1,
          position: "relative",
          bgcolor: "#fff",
          borderRadius: 1,
          height: "44px",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 8,
            bottom: 8,
            background:
              "linear-gradient(90deg, rgba(0,128,128,1) 0%, rgba(68,40,160,1) 100%)",
            borderRadius: 1,
            transition: "all 0.3s ease",
            zIndex: 0,
            ...indicatorStyle,
          }}
        />
        {tabs.map((label, index) => {
          const isActive = activeTab === index;
          return (
            <Box
              key={label}
              ref={(el) => (tabRefs.current[index] = el)}
              onClick={() => {
                setActiveTab(index);
                handleServiceChange(index === 0 ? "internal" : "external");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                py: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: isActive ? "white" : theme.lightTheme.placeholder,
                  transition: "color 0.3s ease",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {activeTab === 0 ? (
        <AssignedTickets
          setApiToggle={setApiToggle}
          locationUserID={locationUserID}
          tickets={assignedTicket}
          assignedToTicket={assignedToTicket}
          followUpTickets={followUpTickets}
          user_id={user_id}
          store_id={store_id}
          followUpStatus={followUpStatus}
          setFollowUpStatus={setFollowUpStatus}
        />
      ) : (
        <RaisedTickets tickets={raisedTicket} />
      )}
    </>
  );
};

export default MyTickets;
